import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import type { GameReviewListRequest } from '../../../../../types/features/games';
import { useGameReviewGames } from '../useGameReviewGames';

const PAGE_SIZE = 100;

export const useMissingImageGamesLogic = (search: string, enabled: boolean) => {
  const normalizedSearch = search.trim();
  const debouncedSearch = useDebouncedValue(normalizedSearch, 500);
  const [filters, setFilters] = useState<Required<GameReviewListRequest>>({
    page: 1,
    state: '',
    search: normalizedSearch,
  });

  useEffect(() => {
    // Apply the search and page reset together to avoid requesting the previous page.
    setFilters((current) => current.search === debouncedSearch
      ? current
      : { ...current, search: debouncedSearch, page: 1 });
  }, [debouncedSearch]);

  const isSearchPending = normalizedSearch !== filters.search;
  const review = useGameReviewGames('missing_images', enabled && !isSearchPending, filters);
  const lastPage = Math.max(1, Math.ceil(review.total / PAGE_SIZE));
  const needsPageCorrection = enabled && !isSearchPending && !review.loading && !review.error
    && filters.page > lastPage;

  useEffect(() => {
    if (!needsPageCorrection) return;
    setFilters((current) => current.page > lastPage ? { ...current, page: lastPage } : current);
  }, [lastPage, needsPageCorrection]);

  const handleStateChange = useCallback((event: SelectChangeEvent<string>) => {
    const state = event.target.value;
    setFilters((current) => ({ ...current, state, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((current) => ({ ...current, page }));
  }, []);

  const pagination = useMemo(() => ({
    onPageChange: handlePageChange,
    page: filters.page,
    rowsPerPage: PAGE_SIZE,
    total: review.total,
  }), [filters.page, handlePageChange, review.total]);

  return {
    ...review,
    loading: enabled && (isSearchPending || needsPageCorrection || review.loading),
    error: isSearchPending ? null : review.error,
    hasSearch: filters.search.length > 0,
    selectedState: filters.state,
    handleStateChange,
    pagination,
  };
};
